/**
 * Centralised Firestore data-access layer.
 *
 * Design principles:
 * - All list queries default to excluding soft-deleted docs (deleted !== true)
 * - `in` queries guard against empty arrays (Firestore throws on empty `in`)
 * - Firestore `in` supports max 30 values per clause — large arrays are chunked
 * - Does NOT import AuthContext to avoid circular dependencies;
 *   callers pass `currentUser` when needed for audit writes
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  writeBatch,
  serverTimestamp,
  getCountFromServer,
} from 'firebase/firestore';
import { db } from '../config/firebase';

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Split an array into chunks of `size`. Used to stay under Firestore's 30-value `in` limit. */
function chunk(arr, size) {
  const chunks = [];
  for (let i = 0; i < arr.length; i += size) {
    chunks.push(arr.slice(i, i + size));
  }
  return chunks;
}

// ── Candidates ────────────────────────────────────────────────────────────────

/**
 * Fetch a paginated, optionally filtered list of candidates.
 *
 * @param {object} filters
 * @param {string[]} [filters.districtNames]  - Filter to specific district names (server-side)
 * @param {string}   [filters.status]         - Filter by conferenceStatus
 * @param {string}   [filters.stage]          - Filter by processStage
 * @param {number}   [filters.pageSize=50]    - Max documents per page
 * @param {import('firebase/firestore').DocumentSnapshot} [filters.startAfterDoc] - Pagination cursor
 * @returns {Promise<{ candidates: object[], lastDoc: DocumentSnapshot|null }>}
 */
export async function getCandidates({
  districtNames,
  status,
  stage,
  pageSize = 1000,
  startAfterDoc = null,
} = {}) {
  // If districtNames is specified but empty, return nothing immediately
  if (districtNames !== undefined && districtNames.length === 0) {
    return { candidates: [], lastDoc: null };
  }

  // If multiple districts, use chunked `in` queries and merge
  if (districtNames && districtNames.length > 1) {
    return getCandidatesByDistricts(districtNames, { status, stage, pageSize, startAfterDoc });
  }

  // Note: combining where('deleted','==',false) with orderBy('lastName') requires a
  // composite index. We sort client-side to avoid the index dependency.
  const constraints = [where('deleted', '==', false)];

  if (districtNames && districtNames.length === 1) {
    constraints.push(where('district', '==', districtNames[0]));
  }
  if (status) constraints.push(where('conferenceStatus', '==', status));
  if (stage) constraints.push(where('processStage', '==', stage));
  if (startAfterDoc) constraints.push(startAfter(startAfterDoc));
  constraints.push(limit(pageSize));

  const q = query(collection(db, 'candidates'), ...constraints);
  const snap = await getDocs(q);
  const candidates = snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .sort((a, b) => (a.lastName || '').localeCompare(b.lastName || ''));
  const lastDoc = snap.docs[snap.docs.length - 1] || null;
  return { candidates, lastDoc };
}

/**
 * Fetch candidates across multiple districts by chunking `in` queries.
 * Merges and sorts results client-side.
 */
export async function getCandidatesByDistricts(districtNames, { status, stage, pageSize = 50 } = {}) {
  if (!districtNames || districtNames.length === 0) {
    return { candidates: [], lastDoc: null };
  }

  const chunks = chunk(districtNames, 30);
  const promises = chunks.map((names) => {
    const constraints = [
      where('deleted', '==', false),
      where('district', 'in', names),
    ];
    if (status) constraints.push(where('conferenceStatus', '==', status));
    if (stage) constraints.push(where('processStage', '==', stage));
    constraints.push(orderBy('lastName', 'asc'));
    constraints.push(limit(pageSize));
    return getDocs(query(collection(db, 'candidates'), ...constraints));
  });

  const snapshots = await Promise.all(promises);
  const candidates = snapshots
    .flatMap((snap) => snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    .sort((a, b) => (a.lastName || '').localeCompare(b.lastName || ''));

  return { candidates, lastDoc: null };
}

/** Fetch a single candidate by Firestore document ID. */
export async function getCandidate(id) {
  const snap = await getDoc(doc(db, 'candidates', id));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}

/**
 * Update a candidate document.
 * Writes an activityLog entry in the same batch for an audit trail.
 *
 * @param {string} id           - Firestore document ID
 * @param {object} data         - Fields to update
 * @param {object} currentUser  - The authenticated user making the change
 * @param {string} [action]     - Human-readable action label (default 'profile_update')
 * @param {object} [before]     - Snapshot of changed fields before the update (for diff)
 */
export async function updateCandidate(id, data, currentUser, action = 'profile_update', before = {}) {
  const batch = writeBatch(db);

  const candidateRef = doc(db, 'candidates', id);
  batch.update(candidateRef, { ...data, updatedAt: serverTimestamp() });

  const stripUndefined = (obj) =>
    Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined));

  const logRef = doc(collection(db, 'candidates', id, 'activityLog'));
  batch.set(logRef, {
    action,
    changedBy: currentUser?.uid || 'unknown',
    changedByRole: currentUser?.role || 'unknown',
    timestamp: serverTimestamp(),
    before: stripUndefined(before),
    after: stripUndefined(data),
  });

  await batch.commit();
}

/**
 * Count candidates matching optional district filter(s).
 * Uses Firestore server-side aggregation (no document reads).
 * Pass either a single `districtName` or multiple `districtNames` (chunked
 * into `in` queries of 30 and summed) — a dCOM user assigned to several
 * districts needs all of them counted, not just one.
 */
export async function countCandidates({ districtName, districtNames, status, stage } = {}) {
  const buildConstraints = (districtWhere) => {
    const constraints = [where('deleted', '==', false)];
    if (districtWhere) constraints.push(districtWhere);
    if (status) constraints.push(where('conferenceStatus', '==', status));
    if (stage) constraints.push(where('processStage', '==', stage));
    return constraints;
  };

  if (districtNames) {
    if (districtNames.length === 0) return 0;
    const chunks = chunk(districtNames, 30);
    const counts = await Promise.all(
      chunks.map(async (names) => {
        const constraints = buildConstraints(where('district', 'in', names));
        const snap = await getCountFromServer(query(collection(db, 'candidates'), ...constraints));
        return snap.data().count;
      })
    );
    return counts.reduce((sum, c) => sum + c, 0);
  }

  const constraints = buildConstraints(districtName ? where('district', '==', districtName) : null);
  const snap = await getCountFromServer(query(collection(db, 'candidates'), ...constraints));
  return snap.data().count;
}

// ── Documents ─────────────────────────────────────────────────────────────────

/** Fetch all non-deleted documents for a candidate. */
export async function getDocumentsByCandidate(candidateId) {
  // candidateId equality filter + orderBy uploadedAt needs a composite index.
  // Sort client-side to avoid the dependency.
  const q = query(
    collection(db, 'documents'),
    where('candidateId', '==', candidateId),
    where('deleted', '==', false)
  );
  const snap = await getDocs(q);
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .sort((a, b) => {
      const aTime = a.uploadedAt?.toMillis?.() || 0;
      const bTime = b.uploadedAt?.toMillis?.() || 0;
      return bTime - aTime;
    });
}

// ── Submissions ───────────────────────────────────────────────────────────────

/** Fetch all submissions for a candidate. */
export async function getSubmissionsByCandidate(candidateId) {
  const q = query(
    collection(db, 'candidateSubmissions'),
    where('candidateId', '==', candidateId),
    orderBy('year', 'desc')
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

// ── Action Reports ────────────────────────────────────────────────────────────

/**
 * Fetch action reports with optional filters.
 *
 * @param {object} filters
 * @param {string} [filters.candidateId]
 * @param {string} [filters.districtName]
 * @param {string} [filters.status]
 * @param {number} [filters.pageSize=100]
 * @param {import('firebase/firestore').DocumentSnapshot} [filters.startAfterDoc]
 */
export async function getActionReports({
  candidateId,
  districtName,
  status,
  pageSize = 100,
  startAfterDoc = null,
} = {}) {
  const constraints = [orderBy('date', 'desc')];
  if (candidateId) constraints.push(where('candidateId', '==', candidateId));
  if (districtName) constraints.push(where('districtName', '==', districtName));
  if (status) constraints.push(where('status', '==', status));
  if (startAfterDoc) constraints.push(startAfter(startAfterDoc));
  constraints.push(limit(pageSize));

  const snap = await getDocs(query(collection(db, 'actionReports'), ...constraints));
  const reports = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  const lastDoc = snap.docs[snap.docs.length - 1] || null;
  return { reports, lastDoc };
}

// ── Soft deletes ──────────────────────────────────────────────────────────────

/**
 * Mark a document as soft-deleted instead of permanently removing it.
 * Writes an activityLog entry for candidates.
 *
 * @param {string} collectionName - e.g. 'documents', 'candidates'
 * @param {string} docId          - Firestore document ID
 * @param {object} currentUser    - Authenticated user
 */
export async function softDelete(collectionName, docId, currentUser) {
  const ref = doc(db, collectionName, docId);
  const payload = {
    deleted: true,
    deletedAt: serverTimestamp(),
    deletedBy: currentUser?.uid || 'unknown',
  };

  if (collectionName === 'candidates') {
    const batch = writeBatch(db);
    batch.update(ref, payload);
    const logRef = doc(collection(db, 'candidates', docId, 'activityLog'));
    batch.set(logRef, {
      action: 'soft_delete',
      changedBy: currentUser?.uid || 'unknown',
      changedByRole: currentUser?.role || 'unknown',
      timestamp: serverTimestamp(),
      before: {},
      after: payload,
    });
    await batch.commit();
  } else {
    await updateDoc(ref, payload);
  }
}
